/**
 * User Preferences Store (Zustand)
 *
 * Write-local-first: preference changes update Zustand + AsyncStorage instantly,
 * then sync to the backend via background queue. No loading/error state for
 * preference mutations (except completeOnboarding which is a navigation gate).
 */

import { create } from 'zustand';
import { api } from '../services/api';
import { createLogger } from '../utils/logger';
import { syncI18nVariant } from '../i18n';
import { findCourseForLocale, getLocaleByCode, getDefaultTTSVoice } from './courseStore';
import { detectSourceLocale } from '../types/locale-detection';
import {
  loadCachedPreferences,
  saveProfileCache,
  updateCachedPreferences,
} from '../services/preferencesCache';
import type { UserProfileCacheData } from '../generated/sync/sync-config';
import { pushPreferences, pushUser } from '../generated/sync/sync-config';
import {
  DEFAULT_LESSON_LANGUAGE,
  FLAGSHIP_COURSE_ID,
} from '../types/locale';
import type {
  LessonLanguage,
  ResolvedCourse,
} from '../types/locale';
const log = createLogger('UserPreferencesStore');



// ── Per-course preference ────────────────────────────────────────

/**
 * 'azure-server' = backend synthesis via POST /api/speech/tts (server-speech-
 * proxy Phase 1) — the default; 'device' = expo-speech. The legacy client-
 * direct 'azure' provider (minted-token on-device synthesis) was retired.
 */
export type TTSProvider = 'device' | 'azure-server';
export type STTProvider = 'device' | 'azure';

export type { ResolvedCourse } from '../types/locale';

export interface CoursePreference {
  courseId: string;
  country: string;
  sourceLocale: string;
  targetLocale: string;
  lessonLanguage: LessonLanguage;
  azureTtsVoice: string;
  ttsProvider: TTSProvider;
  sttProvider: STTProvider;
}

interface UserPreferencesState {
  // Profile
  displayName: string;

  // Voice (user-controlled)
  voiceExercisesEnabled: boolean;

  // Per-course language preferences
  activeCourseId: string | null;
  coursePreferences: CoursePreference[];

  /** Derived: sourceLocale from active course preference */
  sourceLocale: string;
  /** Derived: targetLocale from active course preference */
  targetLocale: string;
  /** Derived: lessonLanguage from active course preference */
  lessonLanguage: LessonLanguage;
  /** Resolved locale metadata for the active course — no optional chaining needed */
  resolvedCourse: ResolvedCourse;

  // Derived: country from active course preference
  country: string | null;
  onboardingCompleted: boolean;

  // Developer
  debugContextSnapshots: boolean;

  // Feedback
  lessonFeedbackEnabled: boolean;

  // Course limits (from server)
  maxCourses: number;

  // Loading / Error (only for completeOnboarding)
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchProfile: () => Promise<void>;
  switchActiveCourse: (courseId: string) => Promise<void>;
  removeCourse: (courseId: string) => Promise<void>;
  updateDisplayName: (displayName: string) => Promise<void>;
  saveLanguagePreferences: (country: string, variant: string, targetLocale: string, lessonLanguage: LessonLanguage, voiceSettings?: { azureTtsVoice: string; ttsProvider: TTSProvider; sttProvider: STTProvider }) => Promise<void>;
  updateVoicePreferences: (enabled: boolean) => Promise<void>;
  updateDebugContextSnapshots: (enabled: boolean) => Promise<void>;
  updateLessonFeedbackEnabled: (enabled: boolean) => Promise<void>;
  loadFromCache: () => Promise<void>;
  completeOnboarding: (country: string, variant: string, targetLocale: string, lessonLanguage: LessonLanguage) => Promise<void>;
  clearError: () => void;
}

/** Build a ResolvedCourse from source + target locale strings.
 *  Throws if locale metadata is missing — seed data guarantees this never happens. */
function resolveCourse(sourceLocale: string, targetLocale: string): ResolvedCourse {
  const match = findCourseForLocale(sourceLocale);
  const sourceMeta = getLocaleByCode(sourceLocale);
  const targetMeta = getLocaleByCode(targetLocale);
  if (!match || !sourceMeta || !targetMeta) {
    throw new Error(`resolveCourse: missing data for ${sourceLocale} → ${targetLocale}`);
  }
  return { course: match.course, sourceLocale: sourceMeta, targetLocale: targetMeta };
}

/** Resolve activeCourseId + derived locale fields from course preferences.
 *  Falls back to first course preference when activeCourseId is null. */
function derivedLocales(
  activeCourseId: string | null,
  coursePreferences: CoursePreference[],
): { activeCourseId: string | null; country: string | null; sourceLocale: string; targetLocale: string; lessonLanguage: LessonLanguage; resolvedCourse: ResolvedCourse } {
  const resolvedId = activeCourseId ?? (coursePreferences.length > 0 ? coursePreferences[0].courseId : null);
  const pref = coursePreferences.find((p) => p.courseId === resolvedId);
  const sourceLocale = pref?.sourceLocale ?? detectSourceLocale();
  const targetLocale = pref?.targetLocale ?? findCourseForLocale(sourceLocale)?.defaultTargetLocale ?? '';
  return {
    activeCourseId: resolvedId,
    country: pref?.country ?? null,
    sourceLocale,
    targetLocale,
    lessonLanguage: (pref?.lessonLanguage ?? DEFAULT_LESSON_LANGUAGE) as LessonLanguage,
    resolvedCourse: resolveCourse(sourceLocale, targetLocale),
  };
}

/** Get the active course preference from current store state. */
export function getActiveCoursePreference(): CoursePreference | undefined {
  const state = useUserPreferencesStore.getState();
  return state.coursePreferences.find((p) => p.courseId === state.activeCourseId);
}

/** Get the resolved course context (non-React accessor). */
export function getResolvedCourse(): ResolvedCourse {
  return useUserPreferencesStore.getState().resolvedCourse;
}

// Compute initial locale defaults once (module load time).
// Type-safety scaffolding ONLY: keeps sourceLocale/targetLocale/resolvedCourse
// non-optional during the pre-onboarding window. Nothing user-visible reads
// these seeded values — onboarding derives its own suggestions (OS language /
// country) and the i18n layer handles "OS matches neither course side"
// explicitly (LOCALE-003-02). detectSourceLocale coerces unknown regions to
// the flagship-course fallback, so resolveCourse below can never throw here.
const _initSource = detectSourceLocale();
const _initTarget = findCourseForLocale(_initSource)?.defaultTargetLocale ?? '';

export const useUserPreferencesStore = create<UserPreferencesState>((set, get) => ({
  // Defaults
  displayName: '',
  voiceExercisesEnabled: true,
  activeCourseId: null,
  coursePreferences: [],
  // Derived locale fields — seeded from bundled data, updated by loadFromCache/fetchProfile.
  lessonLanguage: DEFAULT_LESSON_LANGUAGE,
  sourceLocale: _initSource,
  targetLocale: _initTarget,
  resolvedCourse: resolveCourse(_initSource, _initTarget),
  country: null,
  onboardingCompleted: false,
  debugContextSnapshots: false,
  lessonFeedbackEnabled: false,
  maxCourses: 2,
  isLoading: false,
  error: null,

  /**
   * Load preferences from cache and hydrate the store.
   * Called after a generated refresh function saves raw data to cache.
   */
  loadFromCache: async () => {
    const cached = await loadCachedPreferences();
    if (cached) {
      const cachedCoursePrefs = (cached.coursePreferences ?? []) as CoursePreference[];
      const derived = derivedLocales(cached.activeCourseId, cachedCoursePrefs);
      set({
        displayName: cached.displayName,
        onboardingCompleted: (cached as unknown as { onboardingCompleted?: boolean }).onboardingCompleted ?? false,
        debugContextSnapshots: cached.debugContextSnapshots,
        lessonFeedbackEnabled: cached.lessonFeedbackEnabled,
        voiceExercisesEnabled: cached.voiceExercisesEnabled,
        coursePreferences: cachedCoursePrefs,
        ...derived,
      });
    }
  },

  /**
   * Fetch preferences: hydrate from cache first, then from API.
   */
  fetchProfile: async () => {
    // 1. Hydrate from AsyncStorage caches (instant, pre-network)
    const cached = await loadCachedPreferences();
    if (cached) {
      const cachedCoursePrefs = (cached.coursePreferences ?? []) as CoursePreference[];
      const derived = derivedLocales(cached.activeCourseId, cachedCoursePrefs);
      set({
        displayName: cached.displayName,
        onboardingCompleted: (cached as unknown as { onboardingCompleted?: boolean }).onboardingCompleted ?? false,
        debugContextSnapshots: cached.debugContextSnapshots,
        lessonFeedbackEnabled: cached.lessonFeedbackEnabled,
        voiceExercisesEnabled: cached.voiceExercisesEnabled,
        coursePreferences: cachedCoursePrefs,
        ...derived,
      });
    }

    // 2. Fetch fresh data from API
    // Note: 401 token refresh and USER_NOT_FOUND auto-sync recovery are handled by the Axios interceptor.
    const result = await api.getUserProfile();

    if (!result.ok) {
      // Handle ACCOUNT_DELETED specifically
      if (result.kind === 'domain' && result.error.code === 'ACCOUNT_DELETED') {
        log.info('Account deleted, signing out');
        const { useAuthStore } = await import('./authStore');
        await useAuthStore.getState().logout();
        return;
      }

      if (cached) {
        // Offline with cache — continue silently
        log.info('API unavailable, using cached preferences');
      } else {
        // No cache, no API — log error but don't block
        log.warn('fetchProfile failed without cache', { kind: result.kind, error: result.error });
      }
      return;
    }

    const user = result.data;
    const serverCoursePrefs: CoursePreference[] = ((user.coursePreferences ?? []) as Partial<CoursePreference>[]).map((p) => ({
      courseId: p.courseId ?? '',
      country: p.country ?? '',
      sourceLocale: p.sourceLocale ?? '',
      targetLocale: p.targetLocale ?? '',
      lessonLanguage: p.lessonLanguage ?? 'source',
      azureTtsVoice: p.azureTtsVoice ?? getDefaultTTSVoice(p.targetLocale ?? ''),
      ttsProvider: p.ttsProvider ?? 'azure-server',
      sttProvider: p.sttProvider ?? 'azure',
    }));

    // activeCourseId is device-local — never adopt from server
    const localActiveCourseId = get().activeCourseId;

    // ── Server-owned gates — always adopt ──
    const serverOnboarding = user.onboardingCompleted ?? get().onboardingCompleted;
    set({ onboardingCompleted: serverOnboarding, maxCourses: user.maxCourses ?? 2 });

    // ── Reconciliation (FRESH-003) ──
    // Check if there are pending local changes in the sync queue
    const { syncQueue } = await import('../services/syncQueue');
    const hasPendingChanges = syncQueue.hasPendingForEndpoint('PATCH /api/user/preferences');
    if (hasPendingChanges) {
      // Local changes pending sync — keep local preference values,
      // but merge server course enrollments (tolerant of unknown courses)
      log.info('Local preferences are newer than server, keeping local values');

      // Merge: add server enrollments not in local (enrolled from another device)
      const localCourseIds = new Set(get().coursePreferences.map((p) => p.courseId));
      const newFromServer = serverCoursePrefs.filter((p) => !localCourseIds.has(p.courseId));
      if (newFromServer.length > 0) {
        const merged = [...get().coursePreferences, ...newFromServer];
        set({ coursePreferences: merged });
        await updateCachedPreferences({ coursePreferences: merged });
        log.info('Merged server course enrollments', { new: newFromServer.map((p) => p.courseId) });
      }

      await syncI18nVariant(get().sourceLocale);
    } else {
      // Clean — save raw server response, then derive store state
      await saveProfileCache(user as UserProfileCacheData);

      const derived = derivedLocales(localActiveCourseId, serverCoursePrefs);

      set({
        displayName: user.displayName ?? get().displayName,
        debugContextSnapshots: user.debugContextSnapshots ?? false,
        lessonFeedbackEnabled: user.lessonFeedbackEnabled ?? false,
        voiceExercisesEnabled: user.voiceExercisesEnabled ?? true,
        coursePreferences: serverCoursePrefs,
        maxCourses: user.maxCourses ?? 2,
        ...derived,
      });
      await syncI18nVariant(derived.sourceLocale);
    }

    // Mark profile domain as fresh after successful fetch
    try {
      const { freshnessManager } = await import('../services/freshnessManager');
      freshnessManager.markFresh('userProfile');
    } catch { /* freshnessManager not initialized yet on first call */ }
  },

  /**
   * Update display name (write-local-first)
   */
  updateDisplayName: async (displayName: string) => {
    set({ displayName });
    await updateCachedPreferences({ displayName });
  },

  /**
   * Switch the active course without modifying preferences.
   * Updates activeCourseId + derived locale fields + i18n variant.
   */
  switchActiveCourse: async (courseId: string) => {
    const { coursePreferences, activeCourseId } = get();
    if (courseId === activeCourseId) return;
    const pref = coursePreferences.find((p) => p.courseId === courseId);
    if (!pref) return;

    const derived = derivedLocales(courseId, coursePreferences);
    set(derived);
    await updateCachedPreferences({ activeCourseId: derived.activeCourseId });
    await syncI18nVariant(derived.sourceLocale);
  },

  /**
   * Save all language preferences (write-local-first).
   * country + coursePreferences are auto-enqueued by updateCachedPreferences.
   */
  saveLanguagePreferences: async (country: string, variant: string, targetLocale: string, lessonLanguage: LessonLanguage, voiceSettings?) => {
    const match = findCourseForLocale(variant);
    const courseId = match?.course.id ?? get().activeCourseId ?? FLAGSHIP_COURSE_ID;

    // Upsert course preference — use provided voice settings, preserve existing, or default from locale metadata
    const existing = get().coursePreferences;
    const existingPref = existing.find((p) => p.courseId === courseId);
    const updated = existing.filter((p) => p.courseId !== courseId);
    const azureTtsVoice = voiceSettings?.azureTtsVoice ?? existingPref?.azureTtsVoice ?? getDefaultTTSVoice(targetLocale);
    const ttsProvider = voiceSettings?.ttsProvider ?? existingPref?.ttsProvider ?? 'azure-server';
    const sttProvider = voiceSettings?.sttProvider ?? existingPref?.sttProvider ?? 'azure';
    const pref: CoursePreference = { courseId, country, sourceLocale: variant, targetLocale, lessonLanguage, azureTtsVoice, ttsProvider, sttProvider };
    updated.push(pref);

    set({
      country,
      activeCourseId: courseId,
      coursePreferences: updated,
      sourceLocale: variant,
      targetLocale,
      lessonLanguage,
      resolvedCourse: resolveCourse(variant, targetLocale),
    });
    await updateCachedPreferences({ activeCourseId: courseId, coursePreferences: updated });
    // activeCourseId is device-local — saved to cache only, not synced
    await syncI18nVariant(variant);

    // Notify freshness manager — lesson path may be stale for the new course
    import('../services/freshnessManager').then(({ freshnessManager }) => {
      freshnessManager.markFresh('userProfile'); // local change is the latest
    }).catch(() => {});
  },

  /**
   * Toggle context snapshot storage (write-local-first)
   */
  updateDebugContextSnapshots: async (enabled: boolean) => {
    set({ debugContextSnapshots: enabled });
    await updateCachedPreferences({ debugContextSnapshots: enabled });
  },

  /**
   * Toggle lesson feedback buttons (write-local-first)
   */
  updateLessonFeedbackEnabled: async (enabled: boolean) => {
    set({ lessonFeedbackEnabled: enabled });
    await updateCachedPreferences({ lessonFeedbackEnabled: enabled });
  },

  /**
   * Toggle voice exercises on/off (write-local-first)
   */
  updateVoicePreferences: async (enabled: boolean) => {
    set({ voiceExercisesEnabled: enabled });
    await updateCachedPreferences({ voiceExercisesEnabled: enabled });
  },

  /**
   * Complete onboarding (hybrid — awaits API, keeps loading/error).
   * This is a navigation gate: AppNavigator checks onboardingCompleted.
   */
  completeOnboarding: async (country: string, variant: string, targetLocale: string, lessonLanguage: LessonLanguage) => {
    const match = findCourseForLocale(variant);
    const courseId = match?.course.id ?? FLAGSHIP_COURSE_ID;
    const azureTtsVoice = getDefaultTTSVoice(targetLocale);
    const pref: CoursePreference = { courseId, country, sourceLocale: variant, targetLocale, lessonLanguage, azureTtsVoice, ttsProvider: 'azure-server', sttProvider: 'azure' };

    // Write-local-first: save immediately, enqueue backend sync.
    set({
      country,
      activeCourseId: courseId,
      coursePreferences: [pref],
      sourceLocale: variant,
      targetLocale,
      lessonLanguage,
      resolvedCourse: resolveCourse(variant, targetLocale),
      onboardingCompleted: true,
    });
    await saveProfileCache({
      displayName: get().displayName,
      debugContextSnapshots: get().debugContextSnapshots,
      lessonFeedbackEnabled: get().lessonFeedbackEnabled,
      voiceExercisesEnabled: get().voiceExercisesEnabled,
      coursePreferences: [pref],
    } as UserProfileCacheData, { activeCourseId: courseId });
    pushPreferences({ coursePreferences: [pref] });
    pushUser({ onboardingCompleted: true });
    // activeCourseId is device-local — saved to cache only, not synced
    await syncI18nVariant(variant);

    // Notify freshness manager — local change is the latest
    import('../services/freshnessManager').then(({ freshnessManager }) => {
      freshnessManager.markFresh('userProfile');
    }).catch(() => {});
  },

  /**
   * Remove a course enrollment (write-local-first).
   * Cannot remove the last enrolled course.
   * If the removed course was active, switches to the first remaining course.
   */
  removeCourse: async (courseId: string) => {
    const { coursePreferences, activeCourseId } = get();
    if (coursePreferences.length <= 1) return; // must keep at least one

    const updated = coursePreferences.filter((p) => p.courseId !== courseId);
    if (updated.length === coursePreferences.length) return; // course not found

    const needsSwitch = activeCourseId === courseId;
    const newActiveCourseId = needsSwitch ? updated[0].courseId : activeCourseId;
    const derived = derivedLocales(newActiveCourseId, updated);

    set({
      coursePreferences: updated,
      ...derived,
    });
    await updateCachedPreferences({ activeCourseId: derived.activeCourseId, coursePreferences: updated });
    // coursePreferences auto-enqueued — backend reconciles (deletes courses not in the array)
    if (needsSwitch) {
      await syncI18nVariant(derived.sourceLocale);
    }
  },

  /**
   * Clear error (used by OnboardingScreen)
   */
  clearError: () => {
    set({ error: null });
  },
}));
